import cn from 'classnames';
import { RelatedProduct } from 'models/RelatedProduct';
import { Button } from 'components/Button';
import { Checkbox } from 'components/Shared/Form';
import Markdown from 'components/Shared/Markdown';
import Image from 'components/Shared/Image';
import Price from 'components/Shared/Price';
import { getAccumulatedPrice, getCategoryDisplayNameWithUppercase, getDisplayName } from './utils';
import styles from './addon.scss';
import { ToolTipDialog } from 'components/Shared/ToolTip/themes/BGH-RD3';
import { useDispatch } from 'react-redux';
import { useIntersectionObserver } from 'hooks';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { GtmViewListItem } from 'store/googleTagManager/actions';
import { getAddonListName, mapRelatedProductToGtm } from 'store/googleTagManager/events/utils';
import { AddonProps } from 'views/ProductPage/addons/themes/BGH/addons';

const IMAGE_DIMENSIONS = {
  width: 48,
  height: 48
};

const hasShortDescription = (product: RelatedProduct) => !!product.shortDescription.content;
const stopPropagation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation();
export const SingleAddon = ({ addon, toggleAddon, isMobile }: AddonProps) => {
  const { key, displayName, products } = addon;
  const product = products[0];
  const { productId, title, selected, quantity, price, productImage, shortDescription } = product;

  const accumulatedPrice = getAccumulatedPrice(product);
  const reduxDispatch = useDispatch();

  const { setElementRef } = useIntersectionObserver({
    onBatchVisible: handleElementsVisible
  });

  function handleElementsVisible(elements: TrackedElement<RelatedProduct>[]) {
    const items = elements.map(element => {
      const viewedProduct = element.payload;
      const listName = getAddonListName(viewedProduct.productId, viewedProduct.title);

      return mapRelatedProductToGtm({
        product: viewedProduct,
        listName
      });
    });

    reduxDispatch(GtmViewListItem({ gtmItemProducts: items }));
  }

  const handleClick = () => {
    const payload = {
      groupKey: key,
      productId,
      amount: quantity.amount
    };
    toggleAddon(payload, selected);
  };

  const formattedDisplayName = getDisplayName({
    displayName: title,
    quantity: quantity.amount,
    unit: quantity.unit
  });

  return (
    <Button
      onClick={handleClick}
      appearance="tertiary"
      className={cn(styles.addon, { [styles.selected]: selected })}
      element="div" // for tooltip to work in IE
    >
      <Image className={styles.image} {...productImage} {...IMAGE_DIMENSIONS} />

      <div ref={node => setElementRef({ ref: node, payload: product })} className={styles.name}>
        <span className={styles.addonName}>
          {getCategoryDisplayNameWithUppercase({ name: displayName })}
        </span>
        <span className={styles.productName}>
          {getCategoryDisplayNameWithUppercase({ name: formattedDisplayName })}
        </span>
      </div>

      {hasShortDescription(product) && (
        <div onClick={stopPropagation}>
          <ToolTipDialog
            className={styles.tooltip}
            contentClassName={cn(isMobile ? styles.tooltipContentMobile : styles.tooltipContent)}
            position={'left'}
            text={<Markdown {...shortDescription} />}
          />
        </div>
      )}
      <div className={styles.price}>
        <Price className={styles.newPrice} price={accumulatedPrice} unit={price.currencyUnit} />
      </div>
      <Checkbox
        appearance="tertiary"
        className={styles.checkbox}
        color="charcoal"
        checked={selected}
        tabIndex={-1}
        readOnly
      />
    </Button>
  );
};
